import {
  ActionIcon,
  Button,
  Collapse,
  Group,
  Loader,
  Modal,
  Paper,
  SegmentedControl,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { IconAngle, IconCamera, IconCopy, IconCube, IconDoor, IconPlus, IconTrash, IconWindow } from '@tabler/icons-react';
import { lazy, Suspense, useState } from 'react';
import { useMediaQuery } from '@mantine/hooks';
import {
  calcRoom,
  formatQuantity,
  layoutRoom,
  openingWall,
  sumRooms,
  type MeasureOpening,
  type MeasureRoom,
  type RoomMeasures,
  type WallNumber,
} from '@erp/shared';
import { ContourEditor } from './ContourEditor';
import { PhotoSeriesPanel } from './PhotoSeriesPanel';
import { newSlope, SlopesEditor } from './SlopesEditor';

// three.js весит ~170 КБ gzip — грузим его только при открытии 3D-вида.
const Room3DDialog = lazy(() => import('./Room3DDialog'));
import { RoomPlan, WallUnfold } from './RoomDrawings';

interface Props {
  /** id сохранённой сметы — для фотореалистичных визуализаций, которые хранятся вместе с ней. */
  estimateId?: string;
  rooms: MeasureRoom[];
  onChange: (rooms: MeasureRoom[]) => void;
  canEdit: boolean;
}

export function emptyRoom(index: number, height = '2.7'): MeasureRoom {
  return {
    id: crypto.randomUUID(),
    name: `Помещение ${index}`,
    length: '',
    width: '',
    height,
    floorAreaOverride: null,
    perimeterOverride: null,
    openings: [],
  };
}

function MeasuresSummary({ measures }: { measures: RoomMeasures }) {
  const items: [string, string][] = [
    ['Пол', `${formatQuantity(measures.FLOOR_AREA)} м²`],
    // Потолок показываем, только когда он отличается от пола — у мансарды.
    ...(measures.CEILING_SLOPE_AREA.isZero()
      ? []
      : ([
          ['Потолок', `${formatQuantity(measures.CEILING_AREA)} м²`],
          ['из них скаты', `${formatQuantity(measures.CEILING_SLOPE_AREA)} м²`],
        ] as [string, string][])),
    ['Стены', `${formatQuantity(measures.WALL_AREA)} м²`],
    ['Периметр', `${formatQuantity(measures.PERIMETER)} м`],
    ['Плинтус', `${formatQuantity(measures.BASEBOARD_LENGTH)} м`],
    ['Откосы', `${formatQuantity(measures.SLOPES_LENGTH)} м`],
  ];
  return (
    <Group gap="lg" wrap="wrap">
      {items.map(([label, value]) => (
        <Text key={label} size="sm">
          <Text span c="dimmed" size="sm">
            {label}:{' '}
          </Text>
          <Text span fw={500} size="sm">
            {value}
          </Text>
        </Text>
      ))}
    </Group>
  );
}

type RoomShape = 'rect' | 'contour' | 'manual';

/** Кнопки стен помещаются в строку телефона до шести; дальше — выпадающий список. */
const MAX_WALL_BUTTONS = 6;

/**
 * Где стоит проём — для схемы помещения, на объёмы не влияет. Номера стен те
 * же, что в кружках на плане. Отступ задаётся только одиночному проёму:
 * несколько одинаковых окон одной строкой расставляются по стене равномерно.
 */
function OpeningPlacement({
  opening,
  wallCount,
  canEdit,
  onChange,
}: {
  opening: MeasureOpening;
  wallCount: number;
  canEdit: boolean;
  onChange: (patch: Partial<MeasureOpening>) => void;
}) {
  const single = opening.count.trim() === '1';
  const options = Array.from({ length: wallCount }, (_, i) => ({ value: String(i + 1), label: String(i + 1) }));
  const value = String(openingWall(opening, wallCount));
  const setWall = (v: string | null) => v && onChange({ wall: Number(v) as WallNumber });
  return (
    <Group gap="xs" wrap="wrap" align="flex-end" pl={42}>
      {wallCount <= MAX_WALL_BUTTONS ? (
        <Stack gap={2}>
          <Text size="xs" c="dimmed">
            Стена
          </Text>
          <SegmentedControl size="xs" data={options} value={value} onChange={setWall} disabled={!canEdit} />
        </Stack>
      ) : (
        <Select
          size="xs"
          label="Стена"
          data={options}
          value={value}
          onChange={setWall}
          allowDeselect={false}
          disabled={!canEdit}
          w={80}
        />
      )}
      <TextInput
        size="xs"
        label="От угла, м"
        placeholder={single ? 'авто' : 'равномерно'}
        inputMode="decimal"
        value={single ? (opening.offset ?? '') : ''}
        onChange={(e) => onChange({ offset: e.currentTarget.value || null })}
        disabled={!canEdit || !single}
        w={96}
      />
      {opening.kind === 'WINDOW' && (
        <TextInput
          size="xs"
          label="Подоконник, м"
          placeholder="0,8"
          inputMode="decimal"
          value={opening.sillHeight ?? ''}
          onChange={(e) => onChange({ sillHeight: e.currentTarget.value || null })}
          disabled={!canEdit}
          w={104}
        />
      )}
    </Group>
  );
}

/**
 * Обмерный план сметы. Карточка на помещение, а не широкая таблица: обмеры
 * вводит прораб с телефона прямо на объекте. Итоги по помещению и по всему
 * объекту считаются через @erp/shared — теми же функциями, что и на сервере.
 */
export function MeasurementsEditor({ estimateId, rooms, onChange, canEdit }: Props) {
  // null — пользователь ещё не сворачивал блок: раскрыт, если обмеры есть.
  // Обмеры приходят с сервера уже после монтирования, поэтому не useState(rooms…).
  const [userExpanded, setUserExpanded] = useState<boolean | null>(null);
  const expanded = userExpanded ?? rooms.length > 0;
  // Схемы по умолчанию видны: прораб сразу видит, правильно ли поставил проёмы.
  // Выключаются, когда помещений много и карточки становятся слишком длинными.
  const [showDrawings, setShowDrawings] = useState(true);
  const [room3dId, setRoom3dId] = useState<string | null>(null);
  const isMobile = useMediaQuery('(max-width: 48em)');
  const room3d = rooms.find((r) => r.id === room3dId);
  const layout3d = room3d ? layoutRoom(room3d) : null;
  const [photoRoomId, setPhotoRoomId] = useState<string | null>(null);
  const photoRoom = rooms.find((r) => r.id === photoRoomId);

  function updateRoom(id: string, patch: Partial<MeasureRoom>) {
    onChange(rooms.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  }

  function updateOpening(room: MeasureRoom, index: number, patch: Partial<MeasureOpening>) {
    updateRoom(room.id, { openings: room.openings.map((o, i) => (i === index ? { ...o, ...patch } : o)) });
  }

  function addRoom() {
    // Высота потолков в квартире обычно одна — подставляем из последнего помещения.
    const lastHeight = rooms.at(-1)?.height || '2.7';
    onChange([...rooms, emptyRoom(rooms.length + 1, lastHeight)]);
    setUserExpanded(true);
  }

  function duplicateRoom(room: MeasureRoom) {
    onChange([
      ...rooms,
      {
        ...room,
        id: crypto.randomUUID(),
        name: `${room.name} (копия)`,
        openings: [...room.openings],
        slopes: room.slopes ? [...room.slopes] : null,
      },
    ]);
  }

  // Форма помещения выводится из данных: контур — массив (пусть пустой, пока
  // не нарисован), ручные площадь и периметр — строки (пусть пустые). При смене
  // формы поля других режимов сбрасываются в null, иначе расчёт молча
  // остался бы по старым значениям, которых прораб уже не видит.
  function shapeOf(room: MeasureRoom): RoomShape {
    if (room.contour != null) return 'contour';
    if (room.floorAreaOverride != null || room.perimeterOverride != null) return 'manual';
    return 'rect';
  }

  function setShape(room: MeasureRoom, shape: RoomShape) {
    const reset = { contour: null, floorAreaOverride: null, perimeterOverride: null };
    if (shape === 'rect') updateRoom(room.id, reset);
    else if (shape === 'contour') updateRoom(room.id, { ...reset, contour: [] });
    // Без формы скаты не считаются — не храним их скрытыми от прораба.
    else updateRoom(room.id, { ...reset, floorAreaOverride: '', perimeterOverride: '', slopes: null });
  }

  const totals = sumRooms(rooms);

  return (
    <Paper withBorder p="sm">
      <Group justify="space-between" mb={expanded ? 'sm' : 0}>
        <Group gap="xs">
          <Title order={4}>Обмеры</Title>
          <Text size="sm" c="dimmed">
            {rooms.length > 0 ? `помещений: ${rooms.length}` : 'не заполнены'}
          </Text>
        </Group>
        <Group gap="sm">
          {expanded && rooms.length > 0 && (
            <Switch
              size="xs"
              label="Схемы"
              checked={showDrawings}
              onChange={(e) => setShowDrawings(e.currentTarget.checked)}
            />
          )}
          <Button variant="subtle" size="xs" onClick={() => setUserExpanded(!expanded)}>
            {expanded ? 'Свернуть' : 'Развернуть'}
          </Button>
        </Group>
      </Group>

      <Collapse expanded={expanded}>
        <Stack gap="sm">
          {rooms.map((room) => {
            const shape = shapeOf(room);
            const layout = layoutRoom(room);
            const conflicts = new Set(layout?.openings.filter((o) => o.conflict).map((o) => o.sourceIndex));
            return (
              <Paper key={room.id} withBorder p="sm" bg="var(--mantine-color-default)">
                <Stack gap="xs">
                  <Group gap="xs" wrap="nowrap">
                    <TextInput
                      value={room.name}
                      onChange={(e) => updateRoom(room.id, { name: e.currentTarget.value })}
                      disabled={!canEdit}
                      style={{ flex: 1 }}
                      aria-label="Название помещения"
                    />
                    {canEdit && (
                      <>
                        <ActionIcon variant="subtle" onClick={() => duplicateRoom(room)} aria-label="Копировать помещение">
                          <IconCopy size={16} />
                        </ActionIcon>
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          onClick={() => onChange(rooms.filter((r) => r.id !== room.id))}
                          aria-label="Удалить помещение"
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      </>
                    )}
                  </Group>

                  {canEdit && (
                    <SegmentedControl
                      size="xs"
                      value={shape}
                      onChange={(v) => setShape(room, v as RoomShape)}
                      data={[
                        { value: 'rect', label: 'Прямоугольное' },
                        { value: 'contour', label: 'Свой контур' },
                        { value: 'manual', label: 'Площадь вручную' },
                      ]}
                    />
                  )}

                  <SimpleGrid cols={{ base: 3 }} spacing="xs">
                    {shape === 'contour' ? null : shape === 'manual' ? (
                      <>
                        <TextInput
                          label="Площадь пола, м²"
                          inputMode="decimal"
                          value={room.floorAreaOverride ?? ''}
                          onChange={(e) => updateRoom(room.id, { floorAreaOverride: e.currentTarget.value })}
                          disabled={!canEdit}
                        />
                        <TextInput
                          label="Периметр, м"
                          inputMode="decimal"
                          value={room.perimeterOverride ?? ''}
                          onChange={(e) => updateRoom(room.id, { perimeterOverride: e.currentTarget.value })}
                          disabled={!canEdit}
                        />
                      </>
                    ) : (
                      <>
                        <TextInput
                          label="Длина, м"
                          inputMode="decimal"
                          value={room.length}
                          onChange={(e) => updateRoom(room.id, { length: e.currentTarget.value })}
                          disabled={!canEdit}
                        />
                        <TextInput
                          label="Ширина, м"
                          inputMode="decimal"
                          value={room.width}
                          onChange={(e) => updateRoom(room.id, { width: e.currentTarget.value })}
                          disabled={!canEdit}
                        />
                      </>
                    )}
                    <TextInput
                      label="Высота, м"
                      inputMode="decimal"
                      value={room.height}
                      onChange={(e) => updateRoom(room.id, { height: e.currentTarget.value })}
                      disabled={!canEdit}
                    />
                  </SimpleGrid>

                  {shape === 'contour' && (
                    <ContourEditor
                      contour={room.contour}
                      rectLength={room.length}
                      rectWidth={room.width}
                      canEdit={canEdit}
                      onChange={(contour) => updateRoom(room.id, { contour })}
                    />
                  )}

                  {room.openings.length > 0 && (
                    <Stack gap={4}>
                      {room.openings.map((opening, index) => (
                        <Stack key={index} gap={4}>
                        <Group gap="xs" wrap="nowrap" align="flex-end">
                          <ActionIcon
                            variant="light"
                            size="lg"
                            disabled={!canEdit}
                            onClick={() =>
                              updateOpening(room, index, { kind: opening.kind === 'WINDOW' ? 'DOOR' : 'WINDOW' })
                            }
                            aria-label={opening.kind === 'WINDOW' ? 'Окно (нажмите — дверь)' : 'Дверь (нажмите — окно)'}
                            title={opening.kind === 'WINDOW' ? 'Окно' : 'Дверь'}
                          >
                            {opening.kind === 'WINDOW' ? <IconWindow size={18} /> : <IconDoor size={18} />}
                          </ActionIcon>
                          <TextInput
                            size="xs"
                            label="Ширина"
                            inputMode="decimal"
                            value={opening.width}
                            onChange={(e) => updateOpening(room, index, { width: e.currentTarget.value })}
                            disabled={!canEdit}
                            style={{ flex: 1 }}
                          />
                          <TextInput
                            size="xs"
                            label="Высота"
                            inputMode="decimal"
                            value={opening.height}
                            onChange={(e) => updateOpening(room, index, { height: e.currentTarget.value })}
                            disabled={!canEdit}
                            style={{ flex: 1 }}
                          />
                          <TextInput
                            size="xs"
                            label="Шт."
                            inputMode="numeric"
                            value={opening.count}
                            onChange={(e) => updateOpening(room, index, { count: e.currentTarget.value })}
                            disabled={!canEdit}
                            w={56}
                          />
                          {canEdit && (
                            <ActionIcon
                              variant="subtle"
                              color="red"
                              size="lg"
                              onClick={() => updateRoom(room.id, { openings: room.openings.filter((_, i) => i !== index) })}
                              aria-label="Удалить проём"
                            >
                              <IconTrash size={14} />
                            </ActionIcon>
                          )}
                        </Group>
                        {shape !== 'manual' && (
                          <OpeningPlacement
                            wallCount={layout?.walls.length ?? 4}
                            opening={opening}
                            canEdit={canEdit}
                            onChange={(patch) => updateOpening(room, index, patch)}
                          />
                        )}
                        {conflicts.has(index) && (
                          <Text size="xs" c="seal.6">
                            Проём не помещается на стене или перекрывает другой — проверьте стену и отступ.
                          </Text>
                        )}
                        </Stack>
                      ))}
                    </Stack>
                  )}

                  {shape !== 'manual' && (
                    <SlopesEditor
                      room={room}
                      wallCount={layout?.walls.length ?? 4}
                      canEdit={canEdit}
                      onChange={(slopes) => updateRoom(room.id, { slopes: slopes.length ? slopes : null })}
                    />
                  )}

                  {canEdit && (
                    <Group gap="xs">
                      <Button
                        size="compact-xs"
                        variant="light"
                        leftSection={<IconWindow size={14} />}
                        onClick={() =>
                          updateRoom(room.id, {
                            openings: [...room.openings, { kind: 'WINDOW', width: '1.5', height: '1.4', count: '1' }],
                          })
                        }
                      >
                        Окно
                      </Button>
                      <Button
                        size="compact-xs"
                        variant="light"
                        leftSection={<IconDoor size={14} />}
                        onClick={() =>
                          updateRoom(room.id, {
                            openings: [...room.openings, { kind: 'DOOR', width: '0.9', height: '2.1', count: '1' }],
                          })
                        }
                      >
                        Дверь
                      </Button>
                      {/* У помещения без формы скаты не посчитать — кнопки нет. */}
                      {shape !== 'manual' && (
                        <Button
                          size="compact-xs"
                          variant="light"
                          leftSection={<IconAngle size={14} />}
                          onClick={() =>
                            updateRoom(room.id, {
                              slopes: [...(room.slopes ?? []), newSlope(room, layout?.walls.length ?? 4)],
                            })
                          }
                        >
                          Скат потолка
                        </Button>
                      )}
                    </Group>
                  )}

                  <MeasuresSummary measures={calcRoom(room)} />

                  {/* В отличие от 3D, дизайну по фото обмеры не нужны — кнопка есть всегда. */}
                  <Button
                    size="compact-xs"
                    variant="light"
                    leftSection={<IconCamera size={14} />}
                    onClick={() => setPhotoRoomId(room.id)}
                    w="fit-content"
                  >
                    Дизайн по фото
                  </Button>

                  {/* Для ещё не нарисованного контура подсказка не нужна — выше уже холст. */}
                  {showDrawings &&
                    (layout || shape !== 'contour') &&
                    (layout ? (
                      <Stack gap="xs">
                        <RoomPlan room={room} layout={layout} />
                        {layout.height > 0 && (
                          <>
                            <Button
                              size="compact-xs"
                              variant="light"
                              leftSection={<IconCube size={14} />}
                              onClick={() => setRoom3dId(room.id)}
                              w="fit-content"
                            >
                              Открыть в 3D
                            </Button>
                            <Text size="xs" c="dimmed">
                              Развёртка стен — вид изнутри помещения, по часовой стрелке
                            </Text>
                            <WallUnfold room={room} layout={layout} />
                          </>
                        )}
                      </Stack>
                    ) : (
                      <Text size="xs" c="dimmed">
                        {shape === 'manual'
                          ? 'По одной площади и периметру схему не построить — нарисуйте помещение в режиме «Свой контур».'
                          : 'Схема появится, когда будут заполнены длина и ширина.'}
                      </Text>
                    ))}
                </Stack>
              </Paper>
            );
          })}

          {canEdit && (
            <Button variant="light" size="xs" leftSection={<IconPlus size={14} />} onClick={addRoom} w="fit-content">
              Добавить помещение
            </Button>
          )}

          {rooms.length > 1 && (
            <Stack gap={2}>
              <Text size="sm" fw={600}>
                Итого по объекту
              </Text>
              <MeasuresSummary measures={totals} />
            </Stack>
          )}
        </Stack>
      </Collapse>

      <Modal
        opened={room3d != null && layout3d != null}
        onClose={() => setRoom3dId(null)}
        title={room3d ? `${room3d.name} — 3D` : ''}
        size="80rem"
        fullScreen={isMobile}
        radius={0}
      >
        {room3d && layout3d && (
          <Suspense
            fallback={
              <Group justify="center" p="xl">
                <Loader size="sm" />
              </Group>
            }
          >
            <Room3DDialog
              estimateId={estimateId}
              roomId={room3d.id}
              layout={layout3d}
              finish={room3d.finish}
              canEdit={canEdit}
              onFinishChange={(finish) => updateRoom(room3d.id, { finish })}
            />
          </Suspense>
        )}
      </Modal>

      <Modal
        opened={photoRoom != null}
        onClose={() => setPhotoRoomId(null)}
        title={photoRoom ? `${photoRoom.name} — дизайн по фото` : ''}
        size="80rem"
        fullScreen={isMobile}
        radius={0}
      >
        {photoRoom && <PhotoSeriesPanel estimateId={estimateId} roomId={photoRoom.id} canEdit={canEdit} />}
      </Modal>
    </Paper>
  );
}
